/**
 * Sirket kullanicisi icin SMTP read-only goruntuleme.
 *
 * SuperAdmin olmayan kullanicilar SMTP credentials'ini DEGISTIREMEZ; sadece
 * etkili (resolved) SMTP'nin sifresiz bilgisini gorur:
 *   - Kendi sirketinin ozel SMTP'si var mi (chip)
 *   - Host, Port, SSL, gonderici email/ad
 *   - Sifre var mi (chip)
 *   - Son guncelleme bilgisi (kim, ne zaman)
 *
 * Sifre / kullanici adi degistirilmek isteniyorsa SuperAdmin'e basvurulmalidir.
 */

import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { BRAND } from '../../../../config/brand';
import { getEffectiveSmtp } from '../services/mailReportsApi';
import type { EffectiveSmtp } from '../types';

const CompanySmtpReadOnlyView: React.FC = () => {
  const [smtp, setSmtp] = React.useState<EffectiveSmtp | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getEffectiveSmtp();
        if (!cancel) setSmtp(data);
      } catch (err: any) {
        if (!cancel) setError(err?.response?.data?.Message || err?.message || 'SMTP bilgisi alınamadı.');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <LockIcon color="action" />
        <Box>
          <Typography variant="h6">SMTP Ayarları</Typography>
          <Typography variant="caption" color="text.secondary">
            Bu bölüm salt okunurdur. Değişiklik için {BRAND.contact.email} ile iletişime geçin.
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !smtp ? (
        <Alert severity="warning">
          Bu şirket için etkili bir SMTP yapılandırılmamış. SuperAdmin ile iletişime geçin.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
            {smtp.IsCompanySpecific ? (
              <Chip size="small" label="Şirkete Özel SMTP" color="primary" />
            ) : (
              <Chip size="small" label="Aquasoft Global Default" color="default" />
            )}
            {smtp.IsActive ? (
              <Chip size="small" label="Aktif" color="success" variant="outlined" />
            ) : (
              <Chip size="small" label="Pasif" color="error" variant="outlined" />
            )}
            {smtp.UseSsl ? (
              <Chip size="small" label="SSL/TLS" color="success" variant="outlined" />
            ) : (
              <Chip size="small" label="SSL Yok" color="default" variant="outlined" />
            )}
            <Chip
              size="small"
              label={smtp.HasPassword ? 'Şifre Mevcut' : 'Şifre Yok!'}
              color={smtp.HasPassword ? 'success' : 'error'}
              variant="outlined"
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={8}>
              <FieldRow label="Host" value={smtp.Host} mono />
            </Grid>
            <Grid item xs={4}>
              <FieldRow label="Port" value={String(smtp.Port)} mono />
            </Grid>
            <Grid item xs={6}>
              <FieldRow label="Gönderici Email" value={smtp.FromEmail} mono />
            </Grid>
            <Grid item xs={6}>
              <FieldRow label="Gönderici Adı" value={smtp.FromName} />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={6}>
              <FieldRow
                label="Son Güncelleme"
                value={
                  smtp.UpdatedAt
                    ? new Date(smtp.UpdatedAt).toLocaleString('tr-TR')
                    : '-'
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FieldRow label="Güncelleyen" value={smtp.UpdatedByEmail || '-'} mono />
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

interface FieldProps {
  label: string;
  value: string;
  mono?: boolean;
}

const FieldRow: React.FC<FieldProps> = ({ label, value, mono }) => (
  <Stack>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined }}>
      {value}
    </Typography>
  </Stack>
);

export default CompanySmtpReadOnlyView;
